import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  entityType: string | null;
  entityId: string | null;
  url: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationPreference {
  notificationType: string;
  inAppEnabled: boolean;
  emailEnabled: boolean;
}

const POLL_INTERVAL = 60_000; // 60 seconds

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export function useNotifications() {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<Notification[]>({
    queryKey: ['notifications', workspaceId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<Notification[]>('/v1/notifications', {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export function useUnreadCount() {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<{ count: number }>({
    queryKey: ['notifications-unread', workspaceId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<{ count: number }>('/v1/notifications/unread-count', {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
    refetchInterval: POLL_INTERVAL,
  });
}

export function useMarkRead() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const token = await getToken();
      return api.post(`/v1/notifications/${notificationId}/read`, {}, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread', workspaceId] });
    },
  });
}

export function useMarkAllRead() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return api.post('/v1/notifications/read-all', {}, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread', workspaceId] });
    },
  });
}

export function useDismissNotification() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const token = await getToken();
      return api.delete(`/v1/notifications/${notificationId}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread', workspaceId] });
    },
  });
}

export function useNotificationPreferences() {
  const { getToken, workspaceId } = useApiOpts();
  return useQuery<NotificationPreference[]>({
    queryKey: ['notification-preferences', workspaceId],
    queryFn: async () => {
      const token = await getToken();
      return api.get<NotificationPreference[]>('/v1/notifications/preferences', {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export function useSaveNotificationPreferences() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (preferences: NotificationPreference[]) => {
      const token = await getToken();
      return api.patch('/v1/notifications/preferences', { preferences }, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notification-preferences', workspaceId] });
    },
  });
}
